import { z } from 'zod'

import { compareCivilDate } from '~/models/civilDate'
import {
  civilDateSchema,
  planMasterBgInputSchema,
  planMasterCodeInputSchema,
  planMasterRowValidFromRefine,
  validPeriodRefine,
  validPeriodRefineIssue,
} from '~/pages/AdminOcctoMaster/tabs/_common/schema'

/**
 * 発電所 (= plants[] の 1 エントリ) 入力 schema。
 * gen-sales-plant-master-select 設計書: 発電所マスタ UUID 参照
 * (plant_master_id) に置換。 発電所の行は所属 BG (bgMasterId) を持つ
 * (電源ユニットマスタ 2a): 同じダイアログの BG 情報で選ばれている BG。
 * 未選択 ('') は「所属 BG が BG 情報にありません」の検査 (下の superRefine) が止める
 * (竹 `GenerationSalesScope.ValidateForSave` と同じ)。
 */
const planMasterPlantInputSchema = z.object({
  /** 発電所マスタ UUID */
  plantMasterId: z.string().min(1, '発電所を選択してください'),
  /** 所属 BG (bg_master UUID)。 同じダイアログの BG 情報にある BG に限る */
  bgMasterId: z.string(),
  /** 適用開始日 */
  validFrom: civilDateSchema,
  // クリア時は RHF field-array の制約で `null` が入る (PeriodFields の
  // clearAsNull JSDoc 参照)。`null` を許容し `undefined` へ正規化する。
  /** 適用終了日 (任意) */
  validTo: civilDateSchema
    .nullable()
    .transform((v) => v ?? undefined)
    .optional(),
})

export type PlanMasterPlantInputValues = z.infer<
  typeof planMasterPlantInputSchema
>

/**
 * BG 情報 (= bgs[] の 1 エントリ) 入力 schema。 共通の所属 BG schema (需要調達計画マスタも
 * 使う) の未選択文言だけを、発電販売計画マスタの spec AC-OMG-002 の文言に置き換える。
 */
const generationSalesBgInputSchema = planMasterBgInputSchema.extend({
  bgMasterId: z.string().min(1, 'BGを選択してください'),
})

/**
 * 保存の検査の文言に使うコードの引き当て (id → コード)。 引き当てられない id はそのまま出す。
 * 画面は BG マスタ・広域機関の発電所の一覧から作る。
 */
export type ScopeCodeLookup = {
  bgCodeById?: ReadonlyMap<string, string>
  plantCodeById?: ReadonlyMap<string, string>
}

type CivilDateInput = z.infer<typeof civilDateSchema>

/** 2 つの期間 (日単位の閉区間、適用終了 undefined = 無期限) が 1 日でも重なるか。 */
function periodsOverlap(
  a: { validFrom: CivilDateInput; validTo?: CivilDateInput | undefined },
  b: { validFrom: CivilDateInput; validTo?: CivilDateInput | undefined },
): boolean {
  return (
    (b.validTo === undefined ||
      compareCivilDate(a.validFrom, b.validTo) <= 0) &&
    (a.validTo === undefined || compareCivilDate(b.validFrom, a.validTo) <= 0)
  )
}

/**
 * 発電販売計画マスタ Create / Edit 共通 Zod schema を、保存の検査の文言に使うコードの引き当て付きで作る。
 *
 * Create では submitterMasterId / submitterCode (new) を指定する。
 * Update では id のみで識別し submitterCode / submitterMasterId は変更不可。
 * codes[] / bgs[] / plants[] は full replacement (PUT セマンティクス)。
 *
 * 保存の検査 (電源ユニットマスタ 2a、竹 `GenerationSalesScope.ValidateForSave` と同じ文言):
 * - 同じ BG の BG 情報の行の期間が重なる → 「BG {cd} の行の期間が重なっています」
 * - 同じ系統コードの発電所情報の行の期間が重なる (BG は問わない) → 「系統コード {cd} の行の期間が重なっています」
 * - 発電所の行の所属 BG が BG 情報に無い → 「系統コード {cd} の所属 BG が BG 情報にありません」
 * 重ならなければ、同じ系統コード・同じ BG の行を何行でも置ける (付け替え = 旧い行に適用終了 + 新しい行)。
 * 最後の防御は backend (reason と metadata を返す)。
 */
/* eslint-disable @typescript-eslint/explicit-function-return-type -- zod の合成型は長く、z.infer のため推論に任せる */
export const buildGenerationSalesMasterFormSchema = (
  lookup: ScopeCodeLookup = {},
) =>
  z
    .object({
      /** 計画提出者マスタ UUID (Create のみ使用) */
      submitterMasterId: z.string().min(1, '計画提出者を選択してください'),
      /** OCCTO 5 桁計画提出者コード (Read-only / Create 時スナップショット) */
      submitterCode: z.string().min(1, '計画提出者コードは必須です'),
      /**
       * 備考 (任意)。 proto CEL `max_len 100` 新設 (Create/Update Request の
       * note field のみ、AC-11、3938-occto-master-plan-spec-followup 設計書 §5)。
       */
      note: z
        .string()
        .max(100, '備考は100文字以内で入力してください')
        .optional(),
      /**
       * 取引相手コードリスト。 1 件以上必須 (AC-01、spec AC-OMG-002)。
       */
      codes: z
        .array(planMasterCodeInputSchema)
        .min(1, '取引先を1件以上選択してください'),
      /** BG 情報 (計画提出者が束ねている発電 BG) */
      bgs: z.array(generationSalesBgInputSchema),
      /** 発電所情報 (系統コードと所属 BG) */
      plants: z.array(planMasterPlantInputSchema),
      /** 適用開始日 (必須) */
      validFrom: civilDateSchema,
      /** 適用終了日 (任意) */
      validTo: civilDateSchema.optional(),
    })
    .refine(validPeriodRefine, validPeriodRefineIssue)
    .superRefine((value, ctx) => {
      // 行ごとの期間: 適用終了日が適用開始日より前の行は、保存を押す前に止める
      // (付け替えで旧い行に適用終了日を入れる時の入力誤り。サーバーの検査だけだと文言が汎用になる)。
      for (const kind of ['bgs', 'plants'] as const) {
        for (const [index, row] of value[kind].entries()) {
          if (
            row.validTo !== undefined &&
            compareCivilDate(row.validTo, row.validFrom) < 0
          ) {
            ctx.addIssue({
              code: 'custom',
              message: '適用終了日は適用開始日以降の日付で入力してください',
              path: [kind, index, 'validTo'],
            })
          }
        }
      }
      planMasterRowValidFromRefine(
        value.validFrom,
        value.bgs,
        '発電 BG の適用開始日はレコードの適用開始日以降の日付で入力してください',
        'bgs',
        ctx,
      )
      planMasterRowValidFromRefine(
        value.validFrom,
        value.plants,
        '発電所の適用開始日はレコードの適用開始日以降の日付で入力してください',
        'plants',
        ctx,
      )
      planMasterRowValidFromRefine(
        value.validFrom,
        value.codes,
        '取引先の適用開始日はレコードの適用開始日以降の日付で入力してください',
        'codes',
        ctx,
      )

      const bgCode = (id: string): string => lookup.bgCodeById?.get(id) ?? id
      const plantCode = (id: string): string =>
        lookup.plantCodeById?.get(id) ?? id

      // BG 情報: 同じ BG の行の期間が重なる (後の行に出す)
      for (const [index, bg] of value.bgs.entries()) {
        const earlier = value.bgs
          .slice(0, index)
          .some((o) => o.bgMasterId === bg.bgMasterId && periodsOverlap(o, bg))
        if (earlier) {
          ctx.addIssue({
            code: 'custom',
            message: `BG ${bgCode(bg.bgMasterId)} の行の期間が重なっています`,
            path: ['bgs', index, 'bgMasterId'],
          })
        }
      }

      // 発電所情報: 同じ系統コードの行の期間が重なる (BG は問わない) / 所属 BG が BG 情報に無い
      const bgIdsOfBgInfo = new Set(value.bgs.map((b) => b.bgMasterId))
      for (const [index, plant] of value.plants.entries()) {
        const earlier = value.plants
          .slice(0, index)
          .some(
            (o) =>
              o.plantMasterId === plant.plantMasterId &&
              periodsOverlap(o, plant),
          )
        if (earlier) {
          ctx.addIssue({
            code: 'custom',
            message: `系統コード ${plantCode(plant.plantMasterId)} の行の期間が重なっています`,
            path: ['plants', index, 'plantMasterId'],
          })
        }
        if (plant.bgMasterId === '' || !bgIdsOfBgInfo.has(plant.bgMasterId)) {
          ctx.addIssue({
            code: 'custom',
            message: `系統コード ${plantCode(plant.plantMasterId)} の所属 BG が BG 情報にありません`,
            path: ['plants', index, 'bgMasterId'],
          })
        }
      }
    })

/* eslint-enable @typescript-eslint/explicit-function-return-type */

/** 引き当て無し (id をそのまま文言に出す) の既定の schema。 */
export const generationSalesMasterFormSchema =
  buildGenerationSalesMasterFormSchema()

export type GenerationSalesMasterFormValues = z.infer<
  typeof generationSalesMasterFormSchema
>
